import { useState } from "react";
import { useCustom, useCustomMutation, useDataProvider, usePermissions, useTranslate } from "@refinedev/core";
import { Button, Field, Modal } from "@fawkes/ui";

import { COMPONENT_PER_SLEUTEL } from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";

/**
 * Het onderdeel Chip en paspoort: het chipnummer van het dier, met het paspoortnummer eronder.
 *
 * <p><b>Waarom dit onderdeel meer doet dan twee velden opsturen.</b> Chip en paspoort staan op de
 * basisinformatie van het dier, en die wordt bijgewerkt met één {@code PUT} over de hele kaart. Twee
 * velden opsturen zou de rest daarvan leegmaken. Dit onderdeel stuurt daarom het volledige
 * basisrecord terug zoals het het ophaalde, met alleen die twee velden vervangen -- precies wat de
 * volledige Basisinformatie-kaart doet.
 *
 * <p><b>En de dubbelcheck.</b> Een chipnummer hoort bij één dier. De api heeft daar een aparte
 * bevraging voor, en de grote kaart gebruikt die; zou dit snelle venster dat overslaan, dan laat de
 * tegel binnen wat de kaart tegenhoudt, en dan staan er twee dieren met hetzelfde nummer in de
 * database. Gecontroleerd wordt er vlak vóór het opslaan: dan kan er niets meer tussen komen, en de
 * melding kan het andere dier bij naam noemen.
 */

/**
 * Het antwoord van de dubbelcontrole: hoort dit nummer al bij een ander dier, en welk.
 *
 * <p>Het {@code id} dat de api meestuurt blijft hier buiten beeld. De melding noemt het dier bij naam,
 * en een kaal nummer op het scherm zegt een verzorger niets -- is de naam onbekend, dan is "een ander
 * dier" eerlijker dan "dier 412".
 */
interface ChipDubbel {
  duplicate: boolean;
  naam: string | null;
}

/** Het deel van de basisinformatie dat weer terug moet bij een wijziging. */
export interface Basis {
  naam: string;
  geslacht: string | null;
  gecastreerd: boolean;
  castratieDatum: string | null;
  diersoortId: number | null;
  rasId: number | null;
  kruising: boolean;
  rasKruisingId: number | null;
  vachtId: number | null;
  kleurId: number | null;
  chipPasnummer: string | null;
  paspoortNummer: string | null;
  stamboomNummer: string | null;
  kenmerken: string | null;
  geboortedatum: string | null;
  overledendatum: string | null;
  version: number | null;
}

export function ChipPaspoortOnderdeel({ dierId }: { dierId: number }) {
  const t = useTranslate();
  const { data: rechten } = usePermissions<string[]>();
  const def = COMPONENT_PER_SLEUTEL.chipPaspoort;
  const magWijzigen = !def.recht || (rechten ?? []).includes(def.recht);

  const { data, refetch } = useCustom<Basis>({ url: `animals/${dierId}`, method: "get" });
  const basis = data?.data;
  const [open, setOpen] = useState(false);

  return (
    <>
      <DierOnderdeel
        def={def}
        waarde={basis?.chipPasnummer || null}
        herkomst={basis?.paspoortNummer
          ? t("dierTegel.chip.paspoort", { args: [basis.paspoortNummer] })
          : undefined}
        magSchrijven={magWijzigen}
        onActie={() => setOpen(true)}
      />
      {open && basis ? (
        <ChipWijzigen
          dierId={dierId}
          basis={basis}
          onSluit={() => setOpen(false)}
          onOpgeslagen={() => { setOpen(false); refetch?.(); }}
        />
      ) : null}
    </>
  );
}

/**
 * Chip- en paspoortnummer wijzigen.
 *
 * <p>Vijftien cijfers of leeg: dezelfde grens die de api afdwingt, hier al gecontroleerd zodat de
 * gebruiker niet op een 400 hoeft te wachten om te horen dat hij één cijfer mist. Een wél gevuld maar
 * te kort nummer is geen chip -- het lijkt er alleen op, en dat is erger dan een leeg veld.
 */
export function ChipWijzigen({ dierId, basis, onSluit, onOpgeslagen }: {
  dierId: number;
  basis: Basis;
  onSluit: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: bezig } = useCustomMutation();
  const dataProvider = useDataProvider();
  const [chip, setChip] = useState(basis.chipPasnummer ?? "");
  const [paspoort, setPaspoort] = useState(basis.paspoortNummer ?? "");
  const [fout, setFout] = useState<string | null>(null);
  const [controleert, setControleert] = useState(false);

  const leeg = (v: string) => (v.trim() === "" ? null : v.trim());
  const chipGoed = chip.trim() === "" || /^\d{15}$/.test(chip.trim());

  const stuur = () => {
    mutate(
      {
        url: `animals/${dierId}`,
        method: "put",
        // Het hele basisrecord terug: de api werkt de kaart in één keer bij, dus wat je weglaat wist je.
        values: {
          naam: basis.naam,
          geslacht: basis.geslacht,
          gecastreerd: basis.gecastreerd,
          castratieDatum: basis.gecastreerd ? basis.castratieDatum : null,
          diersoortId: basis.diersoortId,
          rasId: basis.rasId,
          kruising: basis.kruising,
          rasKruisingId: basis.kruising ? basis.rasKruisingId : null,
          dierVachtId: basis.vachtId,
          dierKleurId: basis.kleurId,
          chipPasnummer: leeg(chip),
          paspoortNummer: leeg(paspoort),
          stamboomNummer: basis.stamboomNummer,
          kenmerken: basis.kenmerken,
          geboortedatum: basis.geboortedatum,
          overledendatum: basis.overledendatum,
          version: basis.version,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: onOpgeslagen,
        onError: (e: { statusCode?: number }) =>
          setFout(t(e?.statusCode === 409 ? "dierTegel.chip.conflict" : "dierTegel.chip.fout")),
      },
    );
  };

  const bewaar = async () => {
    setFout(null);
    if (!chipGoed) {
      setFout(t("dierTegel.chip.ongeldig"));
      return;
    }
    const nieuw = chip.trim();
    // Alleen controleren als er iets veranderd is aan de chip; het eigen nummer opnieuw opsturen is
    // geen duplicaat, en de api sluit het dier zelf al uit.
    if (nieuw === "" || nieuw === (basis.chipPasnummer ?? "")) {
      stuur();
      return;
    }
    setControleert(true);
    try {
      // Via de dataprovider en niet met een kale fetch: die draagt het token en de foutafhandeling
      // van de rest van de app, een losse fetch niet.
      const res = await dataProvider().custom!<ChipDubbel>({
        url: `animals/${dierId}/chip-duplicate?chip=${encodeURIComponent(nieuw)}`,
        method: "get",
      });
      const dubbel = res.data;
      if (dubbel?.duplicate) {
        setFout(t("dierTegel.chip.dubbel", { args: [dubbel.naam ?? t("dierTegel.chip.anderDier")] }));
        return;
      }
    } catch {
      // Kan de controle niet draaien, dan slaan we niet op: een chip die misschien al bij een ander
      // dier hoort is precies wat we niet willen vastleggen.
      setFout(t("dierTegel.chip.controleMislukt"));
      return;
    } finally {
      setControleert(false);
    }
    stuur();
  };

  return (
    <Modal open title={t("dierTegel.chip.wijzigen")} onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button onClick={bewaar} disabled={bezig || controleert}>{t("common.save")}</Button>
        </>
      }
    >
      <div className="do-invoer">
        <Field
          label={t("dierTegel.chip.nummer")}
          value={chip}
          inputMode="numeric"
          maxLength={15}
          data-testid="chip-nummer"
          onChange={(e) => setChip(e.target.value.replace(/\D/g, ""))}
        />
        <p className="do-invoer__hint">
          {chip.trim() === "" || chipGoed
            ? t("dierTegel.chip.vijftien")
            : t("dierTegel.chip.nogCijfers", { args: [String(15 - chip.trim().length)] })}
        </p>
        <Field
          label={t("dierTegel.chip.paspoortnummer")}
          value={paspoort}
          maxLength={30}
          data-testid="chip-paspoort"
          onChange={(e) => setPaspoort(e.target.value)}
        />
        {fout ? <p className="do-invoer__fout" role="alert">{fout}</p> : null}
      </div>
    </Modal>
  );
}
